// Talent Types and Interfaces

export enum TalentType {
  None = 0,
  
  /** Reduce 10% of building upgrade costs */
  DiscountOfBuildingUpgradeCosts = 1,
  
  /** Increase troop training speed by 15% */
  TroopTrainingSpeedBoost = 2,
  
  /** Reduce resource consumption by 8% */
  ResourceEfficiency = 3,
  
  /** Increase battle rewards by 12% */
  BattleRewardsBonus = 4,
  
  /** Reduce quest completion time by 20% */
  QuestSpeedBonus = 5,
  
  /** Increase maximum energy by 10 points */
  MaxEnergyIncrease = 6,
}

export interface TalentRequirement {
  ironBuildingLevel?: number;
  goldBuildingLevel?: number;
  barracksLevel?: number;
  playerLevel?: number;
  prerequisiteTalents?: TalentType[];
}

export interface TalentCost {
  cleg?: number;
  bloodGems?: number;
}

// New V2 API Response Types (Tree-based structure)

/** Status enum for talent level */
export enum TalentLevelStatus {
  Locked = 0,
  Available = 1,
  Upgrading = 2,
  Active = 3,
}

/** Individual talent level within a talent */
export interface UserTalentLevelContract {
  id: number; // This is the talentLevelId used for upgrades
  name: string;
  description?: string;
  registerDateTime?: string;
  status: TalentLevelStatus;
  level: number;
  talentId: number;
  duration: number; // in seconds
  canActive: boolean;
  isActive: boolean;
  upgradeCost?: number;
  instantUpgradeCost?: number;
}

/** Talent with tree structure and multiple levels */
export interface UserTalentContract {
  id: number; // talentId
  name: string;
  description?: string;
  imageUrl?: string;
  categoryId?: number;
  type?: number;
  parentId?: number;
  researchBuildingRequiredLevel?: number;
  children: UserTalentContract[];
  levels: UserTalentLevelContract[];
}

/** Raw talent level payload from the API */
export interface TalentLevelApiContract extends UserTalentLevelContract {
  registerDateTime?: string;
  upgradeCost?: number;
  instantUpgradeCost?: number;
}

/** Raw talent payload from the API */
export interface TalentApiContract {
  id: number;
  categoryId?: number;
  name: string;
  description?: string;
  imageUrl?: string;
  parentId?: number;
  type?: number;
  researchBuildingRequiredLevel?: number;
  children?: (TalentApiContract | number | string)[];
  levels: TalentLevelApiContract[];
}

export interface TalentApiError {
  errorNumber?: number;
  errorSeverity?: number;
  errorState?: number;
  errorProcedure?: string;
  errorline?: number; // Some responses use lowercase
  errorLine?: number; // Others return camel-case with capital L
  errorMessage?: string;
  errorType?: number;
  description?: string;
}

/** Response from GetAllTalents endpoint */
export interface GetAllTalentsResponse {
  value?: number;
  error?: TalentApiError | null;
  message?: string;
  result: TalentApiContract[];
}

/** Response from instant upgrade endpoint */
export interface TalentInstantUpgradeResponse {
  value?: number;
  error?: TalentApiError | null;
  message?: string;
}

/** Talent Category */
export interface TalentCategoryContract {
  id: number;
  name: string;
  description?: string;
  registerDateTime?: string;
  parentId?: number;
  parentName?: string;
  image?: string;
}

/** Response from talent category endpoint */
export interface TalentCategoryResponse {
  result: {
    result: TalentCategoryContract[];
    count: number;
  };
}

// Legacy API types (keeping for backward compatibility)
export interface TalentData {
  id: number;
  name: string;
  description: string;
  registerDateTime: string;
  type: TalentType;
  buildingId: number;
  buildingRequiredLevel: number;
  level: number;
  amount: number;
  categoryId: number;
}

export interface PlayerTalentRecord {
  id: number;
  registerDateTime: string;
  talentId: number;
  buildingId: number;
  playerId: number;
  talent: TalentData;
}

export interface PlayerTalentsResponse {
  result: PlayerTalentRecord[];
  count: number;
}

export type AvailableTalentsResponse = TalentData[];

// Processed types for UI components (V2 compatible)

/** Flattened talent for UI display */
export interface PlayerTalent {
  talentId: number; // Changed from TalentType to number to match v2 API
  talentLevelId: number; // The specific level ID for upgrade
  level: number;
  name: string;
  effect: string;
  description: string;
  imageUrl?: string;
  status: TalentLevelStatus;
  duration: number;
  canActive: boolean;
  isActive: boolean;
  maxLevel: number;
  upgradeCost?: number;
  instantUpgradeCost?: number;
  parentTalentId?: number;
  hasChildren: boolean;
  children: PlayerTalent[];
  categoryId?: number;
  researchBuildingRequiredLevel?: number;
  // Legacy fields for backward compatibility
  recordId?: number;
  amount?: number;
}

/** Available talent simplified for UI */
export interface AvailableTalent {
  talentId: number; // Changed from TalentType
  name: string;
  description: string;
  requirements: TalentRequirement;
  cost: TalentCost;
  isAvailable: boolean;
  canAfford?: boolean;
  maxLevel: number;
  currentLevel?: number;
  buildingRequiredLevel: number;
  amount: number;
  categoryId: number;
}

export interface UpgradingTalentInfo {
  talentId: number;
  talentName: string;
  levelId: number;
  levelName: string;
  duration: number;
  registerDateTime?: string;
  upgradeCost?: number;
  instantUpgradeCost?: number;
}

/** Request to upgrade a specific talent level */
export interface TalentUpgradeRequest {
  talentLevelId: number; // Changed to match v2 API
}

/** Response from talent upgrade */
export interface TalentUpgradeResponse {
  success: boolean;
  talentId?: number;
  talentLevelId?: number;
  newLevel?: number;
  message?: string;
  updatedPlayerResources?: {
    gold?: number;
    bloodGems?: number;
    cleg?: number;
  };
}

export interface TalentsState {
  talentTree: UserTalentContract[]; // Raw tree structure from API
  flattenedTalents: PlayerTalent[]; // Flattened for easier UI access
  categories: TalentCategoryContract[];
  loading: boolean;
  upgrading: number | null; // Changed from TalentType to number
  error?: string;
}

// Helper functions for talent calculations
export const getTalentDisplayName = (talentType: TalentType): string => {
  const names: Record<TalentType, string> = {
    [TalentType.None]: "None",
    [TalentType.DiscountOfBuildingUpgradeCosts]: "Building Efficiency",
    [TalentType.TroopTrainingSpeedBoost]: "Swift Training",
    [TalentType.ResourceEfficiency]: "Resource Mastery",
    [TalentType.BattleRewardsBonus]: "Victory Spoils",
    [TalentType.QuestSpeedBonus]: "Quest Mastery",
    [TalentType.MaxEnergyIncrease]: "Energy Reservoir",
  };
  return names[talentType] || "Unknown Talent";
};

export const getTalentIcon = (talentType: TalentType): string => {
  const icons: Record<TalentType, string> = {
    [TalentType.None]: "",
    [TalentType.DiscountOfBuildingUpgradeCosts]: "/assets/images/talents/building-discount.png",
    [TalentType.TroopTrainingSpeedBoost]: "/assets/images/talents/training-speed.png",
    [TalentType.ResourceEfficiency]: "/assets/images/talents/resource-efficiency.png",
    [TalentType.BattleRewardsBonus]: "/assets/images/talents/battle-rewards.png",
    [TalentType.QuestSpeedBonus]: "/assets/images/talents/quest-speed.png",
    [TalentType.MaxEnergyIncrease]: "/assets/images/talents/energy-boost.png",
  };
  return icons[talentType] || "/assets/images/talents/default.png";
};

export const calculateTalentEffect = (talentType: TalentType, level: number): string => {
  // TODO: Replace this with your actual game logic
  // This is a placeholder implementation - you need to define how talent effects are calculated
  switch (talentType) {
    case TalentType.DiscountOfBuildingUpgradeCosts:
      return `Level ${level} effect`; // Replace with your actual calculation
    case TalentType.TroopTrainingSpeedBoost:
      return `Level ${level} effect`; // Replace with your actual calculation
    case TalentType.ResourceEfficiency:
      return `Level ${level} effect`; // Replace with your actual calculation
    case TalentType.BattleRewardsBonus:
      return `Level ${level} effect`; // Replace with your actual calculation
    case TalentType.QuestSpeedBonus:
      return `Level ${level} effect`; // Replace with your actual calculation
    case TalentType.MaxEnergyIncrease:
      return `Level ${level} effect`; // Replace with your actual calculation
    default:
      return "No effect";
  }
};
